import { type RefObject, useEffect } from "react";
import { nextFocus } from "./modalFocus";
import { lastInputWasKeyboard } from "./useModalFocus";

const CARDS = "button:not([disabled])";
const PLAYABLE = 'button:not([disabled]):not([aria-disabled="true"])';

/** Focus is free to take: nowhere in particular, or already in the hand. */
function focusIsIdle(hand: HTMLElement): boolean {
  const active = document.activeElement;
  return active === null || active === document.body || hand.contains(active);
}

/**
 * Playing with the keyboard (review 0027 G1). While it is not your move the cards are disabled
 * and the focus falls off them; every turn meant tabbing back from the top bar. When the move
 * becomes yours the first playable card takes the focus, only for a player whose last input was
 * a key (a focused card lifts: moving focus for mouse and touch players would change what they
 * see), and only when the focus is not busy elsewhere (the reaction picker, a dialog).
 */
export function useHandFocus(hand: RefObject<HTMLElement | null>, yours: boolean): void {
  // The cards are the controls; the fan only lets the arrow keys walk them.
  useEffect(() => {
    const node = hand.current;
    if (node === null) return;
    const onKey = (event: KeyboardEvent) => walk(node, event);
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, [hand]);
  useEffect(() => {
    const node = hand.current;
    if (!yours || node === null || !lastInputWasKeyboard() || !focusIsIdle(node)) return;
    (node.querySelector<HTMLElement>(PLAYABLE) ?? node.querySelector<HTMLElement>(CARDS))?.focus();
  }, [hand, yours]);
}

/** Left and right walk the cards; they go round at the ends. Enter and Space play, as any button. */
function walk(hand: HTMLElement, event: KeyboardEvent): void {
  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
  const cards = Array.from(hand.querySelectorAll<HTMLElement>(CARDS));
  const at = cards.indexOf(document.activeElement as HTMLElement);
  const next = nextFocus(cards.length, at, event.key === "ArrowLeft");
  if (next === null) return;
  event.preventDefault();
  cards[next]?.focus();
}
